import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { invitationApi } from '@/api/invitationApi'
import { ApiError } from '@/api/httpClient'
import { morphToast } from '@/components/ui/Toast'
import { useNotificationsStore, type BillingNotif, type InvitationResponseNotif } from '@/stores/notificationsStore'
import { useNotificationsWS } from '@/hooks/useNotificationsWS'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { useAuthStore } from '@/stores/authStore'
import type { AnnouncementContent, WorkspaceInvitation } from '@/api/types'

export function NotificationBell() {
  const { t } = useTranslation()
  const invitations = useNotificationsStore((s) => s.invitations)
  const responses = useNotificationsStore((s) => s.responses)
  const billing = useNotificationsStore((s) => s.billing)
  const announcements = useNotificationsStore((s) => s.announcements)
  const announcementTest = useNotificationsStore((s) => s.announcementTest)
  const removeInvitation = useNotificationsStore((s) => s.removeInvitation)
  const dismissResponse = useNotificationsStore((s) => s.dismissResponse)
  const dismissBilling = useNotificationsStore((s) => s.dismissBilling)
  const markAnnouncementSeen = useNotificationsStore((s) => s.markAnnouncementSeen)
  const dismissAnnouncement = useNotificationsStore((s) => s.dismissAnnouncement)
  const clearAll = useNotificationsStore((s) => s.clearAll)
  const setAnnouncementTest = useNotificationsStore((s) => s.setAnnouncementTest)
  const [open, setOpen] = useState(false)
  const [respondingId, setRespondingId] = useState<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useNotificationsWS()

  useEffect(() => {
    if (!open) return
    function onMouse(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('mousedown', onMouse)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onMouse)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    announcements.filter((item) => !item.seen_at).forEach((item) => {
      void markAnnouncementSeen(item.id).catch(() => {})
    })
  }, [announcements, markAnnouncementSeen, open])

  async function respond(invitationId: number, accept: boolean, workspaceId?: number) {
    setRespondingId(invitationId)
    try {
      await invitationApi.respond(invitationId, { accept })
      removeInvitation(invitationId)
      morphToast.success(
        accept
          ? t('notifications.invite.accepted', 'Convite aceito!')
          : t('notifications.invite.declined', 'Convite recusado.'),
      )
      if (accept && workspaceId) {
        const { currentWorkspace, loadMembers, bumpWorkspaceListVersion } = useWorkspaceStore.getState()
        // Atualiza a lista de workspaces disponíveis (WorkspacesListPage)
        bumpWorkspaceListVersion()
        // Atualiza memberships no authStore (sidebar, breadcrumb, etc.)
        void useAuthStore.getState().loadMe()
        // Atualiza a lista de membros se estiver no workspace que foi aceito
        if (currentWorkspace?.id === workspaceId) {
          void loadMembers(workspaceId)
        }
      }
    } catch (err) {
      if (err instanceof ApiError) morphToast.fromError(err, t('common.error', 'Erro'))
    } finally {
      setRespondingId(null)
    }
  }

  const count = invitations.length + responses.length + billing.length + announcements.length + (announcementTest ? 1 : 0)
  const dismissibleCount = responses.length + billing.length + announcements.length + (announcementTest ? 1 : 0)

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {/* Botão sino */}
      <button
        type="button"
        aria-label={t('notifications.bell.label', 'Notificações')}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          borderRadius: 6,
          background: open ? 'var(--bg-card)' : 'transparent',
          border: '1px solid var(--border)',
          cursor: 'pointer',
          color: 'var(--text-muted)',
        }}
      >
        <BellIcon />
        {count > 0 && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              minWidth: 16,
              height: 16,
              borderRadius: 999,
              background: '#ef4444',
              color: '#fff',
              fontSize: 9,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
              lineHeight: 1,
              boxShadow: '0 0 0 2px var(--bg-toolbar)',
            }}
          >
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {/* Painel dropdown */}
      {open && (
        <div
          role="dialog"
          aria-label={t('notifications.panel.label', 'Notificações')}
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 340,
            background: 'var(--bg-app)',
            border: '1px solid var(--border)',
            borderRadius: 12,
            boxShadow: 'var(--shadow-modal)',
            zIndex: 1000,
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '12px 16px 10px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
              {t('notifications.panel.title', 'Notificações')}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {dismissibleCount > 0 && (
                <button
                  type="button"
                  onClick={() => void clearAll().catch((error) => morphToast.fromError(error))}
                  aria-label={t('notifications.panel.clearAll', 'Limpar todas')}
                  style={{
                    padding: 0,
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--accent)',
                    cursor: 'pointer',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  {t('notifications.panel.clearAll', 'Limpar todas')}
                </button>
              )}
              {count > 0 && (
                <span
                  style={{
                    fontSize: 10,
                    background: 'var(--bg-toolbar)',
                    border: '1px solid var(--border)',
                    borderRadius: 999,
                    padding: '1px 7px',
                    color: 'var(--text-muted)',
                    fontWeight: 600,
                  }}
                >
                  {count}
                </span>
              )}
            </div>
          </div>

          {/* Conteúdo */}
          {count === 0 ? (
            <div
              style={{
                padding: '32px 16px',
                textAlign: 'center',
                fontSize: 13,
                color: 'var(--text-dim)',
              }}
            >
              {t('notifications.panel.empty', 'Nenhuma notificação.')}
            </div>
          ) : (
            <div style={{ maxHeight: 440, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {invitations.map((inv) => (
                <InviteCard
                  key={inv.id}
                  invitation={inv}
                  isLoading={respondingId === inv.id}
                  onAccept={() => void respond(inv.id, true, inv.workspace)}
                  onDecline={() => void respond(inv.id, false)}
                  t={t}
                />
              ))}
              {announcementTest ? (
                <AnnouncementCard
                  announcement={announcementTest}
                  isTest
                  onDismiss={() => setAnnouncementTest(null)}
                  t={t}
                />
              ) : null}
              {announcements.map((announcement) => (
                <AnnouncementCard
                  key={`announcement-${announcement.id}`}
                  announcement={announcement.announcement}
                  onDismiss={() => void dismissAnnouncement(announcement.id).catch((error) => morphToast.fromError(error))}
                  t={t}
                />
              ))}
              {billing.map((b) => (
                <BillingCard
                  key={b.id}
                  notif={b}
                  onDismiss={() => dismissBilling(b.id)}
                  t={t}
                />
              ))}
              {responses.map((r) => (
                <ResponseCard
                  key={r.id}
                  notif={r}
                  onDismiss={() => dismissResponse(r.id)}
                  t={t}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function AnnouncementCard({
  announcement,
  isTest = false,
  onDismiss,
  t,
}: {
  announcement: AnnouncementContent
  isTest?: boolean
  onDismiss: () => void
  t: ReturnType<typeof useTranslation>['t']
}) {
  const border =
    announcement.severity === 'critical'
      ? 'var(--color-danger-border)'
      : announcement.severity === 'warning'
        ? 'var(--color-warning-border)'
        : 'var(--border)'
  return (
    <article
      style={{
        position: 'relative',
        padding: '12px 14px',
        background: 'var(--bg-popup)',
        border: `1px solid ${border}`,
        borderRadius: 10,
      }}
    >
      <button
        type="button"
        aria-label={t('common.dismiss', 'Dispensar')}
        onClick={onDismiss}
        style={{ position: 'absolute', top: 8, right: 10, color: 'var(--text-dim)', fontSize: 16 }}
      >
        ×
      </button>
      <strong style={{ display: 'block', paddingRight: 20, fontSize: 13, color: 'var(--text)' }}>
        {isTest ? `${t('announcements.test', 'TESTE')} · ` : ''}{announcement.title}
      </strong>
      <p style={{ margin: '6px 20px 0 0', fontSize: 12, lineHeight: 1.5, color: 'var(--text-muted)' }}>
        {announcement.body}
      </p>
      {announcement.cta_text && announcement.cta_url ? (
        <a
          href={announcement.cta_url}
          target={announcement.cta_url.startsWith('/') ? undefined : '_blank'}
          rel="noreferrer"
          style={{ display: 'inline-block', marginTop: 8, color: 'var(--accent)', fontSize: 12, fontWeight: 600 }}
        >
          {announcement.cta_text}
        </a>
      ) : null}
    </article>
  )
}

/* ── Card de convite recebido ─────────────────────────────────────────────── */

function InviteCard({
  invitation: inv,
  isLoading,
  onAccept,
  onDecline,
  t,
}: {
  invitation: WorkspaceInvitation
  isLoading: boolean
  onAccept: () => void
  onDecline: () => void
  t: ReturnType<typeof useTranslation>['t']
}) {
  const inviterName = inv.invited_by.name || inv.invited_by.email
  const roleLabel =
    inv.role === 1
      ? t('projectRole.owner', 'Owner')
      : inv.role === 2
        ? t('projectRole.admin', 'Admin')
        : inv.role === 3
          ? t('members.role.employee', 'Collaborator')
          : String(inv.role)
  return (
    <div
      style={{
        background: 'var(--bg-popup)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        padding: '12px 14px',
      }}
    >
      {/* Mensagem com chips */}
      <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 2, marginBottom: 12 }}>
        <InlineMessage
          parts={[
            <Chip key="inviter">{inviterName}</Chip>,
            t('notifications.invite.message', {
              inviter: '__inviter__',
              workspace: '__workspace__',
              role: '__role__',
              defaultValue: '__inviter__ está te convidando para o workspace __workspace__ como __role__.',
            }),
            <Chip key="workspace">{inv.workspace_name}</Chip>,
            <Chip key="role" accent>
              {roleLabel}
            </Chip>,
          ]}
        />
      </div>

      {/* Ações */}
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          type="button"
          disabled={isLoading}
          onClick={onAccept}
          style={{
            flex: 1,
            padding: '6px 0',
            borderRadius: 7,
            border: 'none',
            background: 'var(--accent)',
            color: '#fff',
            fontSize: 12,
            fontWeight: 600,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.6 : 1,
          }}
        >
          {isLoading ? t('notifications.invite.loading', 'Respondendo...') : t('notifications.invite.accept', 'Aceitar')}
        </button>
        <button
          type="button"
          disabled={isLoading}
          onClick={onDecline}
          style={{
            flex: 1,
            padding: '6px 0',
            borderRadius: 7,
            border: '1px solid var(--border)',
            background: 'transparent',
            color: 'var(--text-muted)',
            fontSize: 12,
            fontWeight: 500,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.6 : 1,
          }}
        >
          {t('notifications.invite.decline', 'Recusar')}
        </button>
      </div>
    </div>
  )
}

/* ── Card de resposta (para quem convidou) ───────────────────────────────── */

/* ── Card de cobranca (ativacao ou falha de pagamento) ────────────────────── */

function BillingCard({
  notif: b,
  onDismiss,
  t,
}: {
  notif: BillingNotif
  onDismiss: () => void
  t: ReturnType<typeof useTranslation>['t']
}) {
  const activated = b.kind === 'activated'
  return (
    <div
      style={{
        background: 'var(--bg-popup)',
        border: `1px solid ${activated ? 'var(--color-success-border, #86efac)' : 'var(--color-danger-border)'}`,
        borderRadius: 10,
        padding: '12px 14px',
        position: 'relative',
      }}
    >
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('common.dismiss', 'Dispensar')}
        style={{
          position: 'absolute',
          top: 8,
          right: 10,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--text-dim)',
          fontSize: 16,
          lineHeight: 1,
          padding: 0,
        }}
      >
        ×
      </button>

      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', paddingRight: 18 }}>
        <span style={{ fontSize: 18, lineHeight: 1.2 }}>{activated ? '🎉' : '⚠️'}</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <strong style={{ fontSize: 13, color: 'var(--text)' }}>
            {activated
              ? t('notifications.billing.activatedTitle', 'Obrigado por assinar!')
              : t('notifications.billing.failedTitle', 'Falha no pagamento')}
          </strong>
          <span style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            {activated
              ? t('notifications.billing.activatedBody', {
                  plan: b.plan_name,
                  workspace: b.workspace_name,
                  defaultValue:
                    'Seu plano {{plan}} está ativo no workspace {{workspace}}. Bom proveito!',
                })
              : t('notifications.billing.failedBody', {
                  workspace: b.workspace_name,
                  defaultValue:
                    'Não conseguimos processar a cobrança do workspace {{workspace}}. Atualize a forma de pagamento.',
                })}
          </span>
        </div>
      </div>
    </div>
  )
}

function ResponseCard({
  notif: r,
  onDismiss,
  t,
}: {
  notif: InvitationResponseNotif
  onDismiss: () => void
  t: ReturnType<typeof useTranslation>['t']
}) {
  const accepted = r.response === 'accepted'
  const responseMessage = accepted
    ? t('notifications.invite.responseAccepted', {
        invitee: '__invitee__',
        workspace: '__workspace__',
        defaultValue: '__invitee__ aceitou o convite para o workspace __workspace__.',
      })
    : t('notifications.invite.responseDeclined', {
        invitee: '__invitee__',
        workspace: '__workspace__',
        defaultValue: '__invitee__ recusou o convite para o workspace __workspace__.',
      })
  return (
    <div
      style={{
        background: 'var(--bg-popup)',
        border: `1px solid ${accepted ? 'var(--color-success-border, #86efac)' : 'var(--border)'}`,
        borderRadius: 10,
        padding: '12px 14px',
        position: 'relative',
      }}
    >
      {/* Dismiss */}
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('common.dismiss', 'Dispensar')}
        style={{
          position: 'absolute',
          top: 8,
          right: 10,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--text-dim)',
          fontSize: 16,
          lineHeight: 1,
          padding: 0,
        }}
      >
        ×
      </button>

      {/* Texto com chips */}
      <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 2, paddingRight: 18 }}>
        <InlineMessage
          parts={[
            <Chip key="invitee">{r.invitee_name || r.invitee_email}</Chip>,
            responseMessage,
            <Chip key="workspace">{r.workspace_name}</Chip>,
          ]}
        />
      </div>
    </div>
  )
}

function InlineMessage({ parts }: { parts: ReactNode[] }) {
  const placeholders = ['__inviter__', '__workspace__', '__role__', '__invitee__']
  const template = parts.find((part): part is string => typeof part === 'string') ?? ''
  const replacements = parts.filter((part) => typeof part !== 'string')

  if (!template) {
    return <>{parts}</>
  }

  const segments = template.split(new RegExp(`(${placeholders.join('|')})`, 'g')).filter(Boolean)
  let replacementIndex = 0

  return (
    <>
      {segments.map((segment, index) =>
        placeholders.includes(segment) ? (
          <Fragment key={`${segment}-${index}`}>{replacements[replacementIndex++]}</Fragment>
        ) : (
          <Fragment key={`${segment}-${index}`}>{segment}</Fragment>
        ),
      )}
    </>
  )
}

/* ── Chip inline ─────────────────────────────────────────────────────────── */

function Chip({ children, accent }: { children: ReactNode; accent?: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        verticalAlign: 'middle',
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1,
        padding: '3px 7px',
        borderRadius: 5,
        background: accent ? 'color-mix(in srgb, var(--accent) 15%, transparent)' : 'var(--bg-toolbar)',
        color: accent ? 'var(--accent)' : 'var(--text)',
        border: `1px solid ${accent ? 'color-mix(in srgb, var(--accent) 35%, transparent)' : 'var(--border)'}`,
        whiteSpace: 'nowrap',
        maxWidth: 160,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {children}
    </span>
  )
}

/* ── Ícone sino ──────────────────────────────────────────────────────────── */

function BellIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  )
}
